import type { BioContent } from "@fitties/shared";
import { BioMessageChain } from "@shared/components/BioMessageChain";

const GENDER_LABELS: Record<"female" | "male" | "nonbinary", string> = {
  female: "Female",
  male: "Male",
  nonbinary: "Other",
};

const DESIRED_GENDER_LABELS: Record<"female" | "male" | "nonbinary", string> = {
  female: "Women",
  male: "Men",
  nonbinary: "Other",
};

interface ProfileInfoProps {
  displayName: string;
  bio: string;
  bioContent?: BioContent | null | undefined;
  age: number | null;
  gender: "female" | "male" | "nonbinary";
  desiredGenders: ("female" | "male" | "nonbinary")[];
}

/**
 * Profile information display component showing user details in read-only mode.
 *
 * @example
 * ```tsx
 * <ProfileInfo
 *   displayName="John Doe"
 *   bio="Love fitness and hiking"
 *   age={29}
 *   gender="male"
 *   desiredGenders={["female"]}
 * />
 * ```
 */
export default function ProfileInfo({ bio, bioContent, age, gender, desiredGenders }: ProfileInfoProps) {
  const interestedInText =
    desiredGenders.length === 3 ? "Everyone" : desiredGenders.map((g) => DESIRED_GENDER_LABELS[g]).join(", ");

  return (
    <div className="space-y-6" data-testid="profile-info">
      <div>
        <div className="text-gray-600" data-testid="profile-info-bio">
          <span className="font-mono font-semibold">About me:</span>
          <BioMessageChain className="mt-2" compact bio={bio} bioContent={bioContent} emptyLabel="" />
        </div>
      </div>

      {/* Age */}
      {age !== null && (
        <div>
          <p className="text-gray-600">
            <span className="font-mono font-semibold">Age:</span> <span data-testid="profile-info-age">{age}</span>
          </p>
        </div>
      )}

      {/* Gender */}
      <div>
        <p className="text-gray-600">
          <span className="font-mono font-semibold">Gender:</span>{" "}
          <span data-testid="profile-info-gender">{GENDER_LABELS[gender]}</span>
        </p>
      </div>

      {/* Interested in (plain text) */}
      {desiredGenders.length > 0 && (
        <div>
          <p className="text-gray-600" data-testid="profile-info-interested-in">
            <span className="font-mono font-semibold">Interested in:</span> {interestedInText}
          </p>
        </div>
      )}
    </div>
  );
}
